import { ReactNode } from 'react';
import { BaseIssue } from 'valibot';

import {
	Box,
	FormHelperText,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';

import { useFieldContext } from '#form';

interface InputProps<T extends string | boolean | number> {
	label: ReactNode;
	subLabel?: string;
	exclusive?: true;
	options: { [key: string]: T };
	disabledValues?: NoInfer<T>[];
	disabled?: boolean;
}

const defaultDisabledValues: never[] = [];

export function ToggleInput<T extends string | boolean | number>({
	label,
	subLabel,
	field,
	exclusive,
	options,
	disabledValues = defaultDisabledValues,
	disabled = false,
}: InputProps<T> & {
	field?: ReturnType<typeof useFieldContext<T | null | undefined>>;
}) {
	const formField = useFieldContext<T | null | undefined>();

	field ??= formField;

	const error = (
		field.state.meta as {
			errors?: Array<string | BaseIssue<unknown>>;
		}
	).errors?.[0];

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 0.5,
				width: '100%',
			}}
		>
			<Typography
				variant="subtitle2"
				component="span"
				sx={{
					color: 'rgba(0, 0, 0, 0.6)',
					fontSize: '0.875rem',
					cursor: 'default',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				{label}
				{subLabel && (
					<Typography
						variant="body2"
						component="span"
						sx={{
							fontSize: 12,
							marginBottom: 0.5,
						}}
					>
						{subLabel}
					</Typography>
				)}
			</Typography>
			<ToggleButtonGroup
				id={field.name}
				disabled={disabled}
				aria-label="Options"
				value={field.state.value ?? null}
				exclusive={exclusive}
				onChange={(_, value: T) => {
					field.handleChange(value);
				}}
				sx={[
					{
						'& .MuiToggleButton-standard': {
							height: '52px',
							paddingTop: '7px',
						},

						'&& .Mui-selected': {
							backgroundColor: '#4F4F55',
							color: 'primary.contrastText',
							borderColor: 'currentColor',
							'&:hover': {
								backgroundColor: 'primary.dark',
							},
							'&::after': {
								content: '""',
								position: 'absolute',
								bottom: '25%',
								left: '50%',
								width: '55%',
								height: '2px',
								backgroundColor: 'lightblue',
								transform: 'translateX(-50%)',
								filter: 'drop-shadow(0 0 2px lightblue)',
							},
						},
					},
					error != null && {
						'&& .MuiToggleButtonGroup-grouped': {
							borderTopColor: 'error.main',
							borderBottomColor: 'error.main',
						},
						'&& .MuiToggleButtonGroup-firstButton': {
							borderLeftColor: 'error.main',
						},
						'&& .MuiToggleButtonGroup-lastButton': {
							borderRightColor: 'error.main',
						},
					},
				]}
			>
				{Object.entries(options).map(([key, value]) => (
					<ToggleButton
						key={key}
						value={value}
						disabled={disabledValues.includes(value)}
					>
						{key}
					</ToggleButton>
				))}
			</ToggleButtonGroup>
			<FormHelperText error>
				{error ?
					typeof error === 'string' ?
						error
					:	error.message
				:	''}
			</FormHelperText>
		</Box>
	);
}
